import { Box, Card, Chip, Stack, Typography, useTheme } from '@mui/material';
import { Image } from '@realadvisor/image';
import { Building2Icon } from 'lucide-react';

import { gql } from '@graphql/__generated__';
import type { TransactionCardFragment } from '@graphql/__generated__/graphql';
import { NoPhotosPlaceholder } from '@modules/_shared/components/NoPhotoPlaceholder';
import { UserChip } from '@modules/_shared/components/UserChip';
import { useAppData } from '@modules/_shared/providers/AppDataProvider';
import {
  formatAddress,
  formatTimeAgo,
} from '@modules/_shared/utils/formatting';
import { useLocale } from '@shared/hooks/locale';
import { getCurrencyByCountryCode } from '@shared/LocaleContext';
import { formatPrice } from '@shared/utils/format-price';

export const TRANSACTION_CARD_FRAGMENT = gql(/* GraphQL */ `
  fragment TransactionCard on property_transactions {
    id
    purchase_price
    signature_date
    seller_broker {
      id
      full_name
      ...UserChip
    }
    seller_broker_team {
      id
      name
      listings_agency_names
    }
    property_transactions_listings {
      listing_id
    }
    property {
      id
      property_type {
        name
        id
        label
      }
      route
      street_number
      postcode
      locality
      country_code
      computed_surface
      living_surface
      land_surface
      number_of_rooms
      lat
      lng
      locality_place {
        id
      }
      state_place {
        id
      }
      cover_images: property_images(order_by: { order_nr: asc }, limit: 1) {
        id
        image {
          id
          url
        }
      }
    }
  }
`);

type TransactionCardProps = {
  transaction: TransactionCardFragment;
};

const PropertyCharacteristics = ({
  property,
}: {
  property: TransactionCardFragment['property'];
}) => {
  const { t } = useLocale();
  const theme = useTheme();
  return (
    <Typography
      sx={{
        ...theme.typography.body2,
        color: (theme.vars ?? theme).palette.grey['800'],
        fontSize: '0.8rem',
      }}
    >
      <Box>
        {[
          property.number_of_rooms != null &&
            t('roomsCount', { count: property.number_of_rooms }),
          `${property.computed_surface ?? 0} m²`,
          property.land_surface != null &&
            t('plotSurfaceOf', {
              plotSurface: property.land_surface ?? '',
            }),
        ]
          .filter(Boolean)
          .join(' · ')}
      </Box>
    </Typography>
  );
};

// Transaction card component
export const TransactionCard = ({ transaction }: TransactionCardProps) => {
  const { t, locale, dateLocale } = useLocale();
  const { me } = useAppData();
  const theme = useTheme();
  const tenantCountryCode = me?.tenant.country_code;

  const property = transaction.property;

  if (!property || !transaction) {
    return null;
  }

  const defaultCurrency = getCurrencyByCountryCode(tenantCountryCode ?? 'CH');
  const { timeAgo } = formatTimeAgo(
    transaction.signature_date,
    true,
    true,
    t,
    dateLocale,
    true,
    true,
  );

  return (
    <Card
      sx={{
        display: 'flex',
        position: 'relative',
        flex: 1,
        overflow: 'hidden',
      }}
    >
      <Box
        sx={[
          {
            background: (theme.vars ?? theme).palette.grey[200],
            position: 'relative',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            width: { xs: '124px', sm: '160px' },
            minWidth: { xs: '124px', sm: '160px' },
          },
        ]}
      >
        {transaction.property?.cover_images &&
        transaction.property?.cover_images.length > 0 ? (
          <Box sx={{ flexGrow: 1, height: 0 }}>
            <Image src={transaction.property.cover_images[0].image.url ?? ''} />
          </Box>
        ) : (
          <NoPhotosPlaceholder ratio={1} />
        )}
      </Box>
      {transaction?.signature_date && timeAgo && (
        <Box
          sx={{
            display: 'flex',
            position: 'absolute',
            top: 0,
            left: 0,
            m: 0.5,
            zIndex: 1,
            maxWidth: '180px',
          }}
        >
          <Chip
            label={timeAgo}
            size="small"
            sx={{
              bgcolor: 'error.main',
              color: 'error.contrastText',
              maxWidth: '140px',
              '& .MuiChip-label': {
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                display: 'block',
              },
            }}
          />
        </Box>
      )}
      <Stack
        sx={{
          display: 'flex',
          px: 2,
          py: 1,
          flexDirection: 'column',
          flexGrow: 1,
          width: '100%',
        }}
      >
        <Typography
          variant="subtitle1"
          sx={{ fontWeight: 'bold', lineHeight: 1.3 }}
        >
          {property?.property_type?.label ?? ''}
        </Typography>

        <Typography
          sx={{
            ...theme.typography.body2,
            color: (theme.vars ?? theme).palette.grey['800'],
            fontSize: '0.8rem',
          }}
        >
          <PropertyCharacteristics property={property} />

          {formatAddress(transaction.property, null).join(', ')}
        </Typography>

        <Stack
          sx={{
            mt: 1,
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 'bold', lineHeight: 1.3 }}>
            {formatPrice(
              transaction.purchase_price,
              locale,
              defaultCurrency,
              0,
              '-',
            )}{' '}
          </Typography>
          <Typography
            sx={{
              ...theme.typography.body2,
              color: (theme.vars ?? theme).palette.grey['800'],
            }}
          >
            {formatPrice(
              Math.round(
                (transaction.purchase_price ?? 0) /
                  (property.computed_surface ?? 0),
              ),
              locale,
              defaultCurrency,
            )}{' '}
            / m²
          </Typography>
        </Stack>
        {transaction?.seller_broker && (
          <Box
            sx={{
              mt: 1,
            }}
          >
            <Stack
              direction="row"
              sx={{
                gap: 1,
                flexWrap: 'wrap',
              }}
            >
              <UserChip user={transaction.seller_broker} />
              {transaction.seller_broker_team && (
                <Chip
                  icon={<Building2Icon size={18} />}
                  label={transaction.seller_broker_team.name}
                  variant="outlined"
                  sx={{
                    maxWidth: '100%',
                    '& .MuiChip-label': {
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    },
                  }}
                />
              )}
            </Stack>
          </Box>
        )}
      </Stack>
    </Card>
  );
};
